import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Trophy, PhoneCall, MessageSquare, Timer, Search } from "lucide-react";
import ReportFilterBar from "@/components/ops/ReportFilterBar";
import LoadingOverlay from "@/components/common/LoadingOverlay";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSortableTable, SortableTh } from "@/lib/useSortableTable";

const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);
const fmtDays = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}d`);

function Kpi({ label, value, sub, icon: Icon, testId }) {
  return (
    <div className="glass-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
          <div className="kpi-num text-3xl mt-1 text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">
          <Icon className="w-5 h-5 text-slate-700" />
        </div>
      </div>
    </div>
  );
}

function ScoreBar({ value }) {
  const pct = Math.min(100, Math.max(0, Number(value) || 0));
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300"
             style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs tabular-nums text-slate-600 w-10 text-right">{pct.toFixed(1)}</div>
    </div>
  );
}

export default function AgentScorecardPage() {
  const [params, setParams] = useState({});
  const [minCalls, setMinCalls] = useState(10);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/ops/reports/agent-scorecard", { params: { ...params, min_calls: minCalls } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [params, minCalls]);

  const searched = useMemo(() => {
    const rows = data?.rows || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => (r.agent_name || "").toLowerCase().includes(needle) ||
                              (r.team_name || "").toLowerCase().includes(needle));
  }, [data, q]);

  const { rows: filtered, thProps } = useSortableTable(searched, {
    initialKey: "scorecard_score", initialDir: "desc",
    accessors: {
      agent_name: (r) => (r.agent_name || "").toLowerCase(),
      team_name: (r) => (r.team_name || "").toLowerCase(),
    },
  });

  const totals = data?.totals || {};

  return (
    <div className="space-y-6" data-testid="scorecard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">
            Agent scorecard
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Contact rate, conversion on contactable, SMS-vs-voice mix and average time-to-convert per agent.
          </p>
        </div>
        <ReportFilterBar onChange={setParams} testIdPrefix="scorecard" />
      </div>

      {/* Header KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi
          label="Agents in scope"
          value={fmt(data?.agent_count)}
          sub={`Min ${minCalls} calls to qualify`}
          icon={Trophy}
          testId="scorecard-kpi-agents"
        />
        <Kpi
          label="Contact rate"
          value={fmtPct(totals.contactable_pct)}
          sub={`${fmt(totals.contactable)} of ${fmt(totals.total_calls)} calls`}
          icon={PhoneCall}
          testId="scorecard-kpi-contact"
        />
        <Kpi
          label="SMS vs Voice"
          value={`${fmtPct(totals.sms_ratio_of_conversions)} · ${fmtPct(totals.voice_ratio_of_conversions)}`}
          sub={`${fmt(totals.total_converted)} total conversions`}
          icon={MessageSquare}
          testId="scorecard-kpi-mix"
        />
        <Kpi
          label="Avg time-to-convert"
          value={fmtDays(totals.avg_time_to_convert_days)}
          sub="Across all measured conversions"
          icon={Timer}
          testId="scorecard-kpi-ttc"
        />
      </div>

      {/* Controls */}
      <div className="glass-card p-4 flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Search</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Agent or team…"
                className="glass-input h-9 w-[220px] pl-8"
                data-testid="scorecard-search"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Min calls</Label>
            <Input
              type="number"
              min={0}
              value={minCalls}
              onChange={(e) => setMinCalls(Math.max(0, parseInt(e.target.value || "0", 10)))}
              className="glass-input h-9 w-[110px]"
              data-testid="scorecard-min-calls"
            />
          </div>
        </div>
        <div className="text-xs text-slate-500">
          Score = contact-rate × conversion-on-contactable. Higher is better.
        </div>
      </div>

      {/* Table */}
      <LoadingOverlay loading={loading} testId="scorecard-loading">
        <div className="glass-card p-2" data-testid="scorecard-table">
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead data-sticky className="border-b border-slate-100 text-xs uppercase">
                <tr>
                  <SortableTh {...thProps("agent_name", "left")} data-testid="scorecard-th-agent_name">Agent</SortableTh>
                  <SortableTh {...thProps("total_calls")} data-testid="scorecard-th-total_calls">Calls</SortableTh>
                  <SortableTh {...thProps("contactable_pct")} data-testid="scorecard-th-contactable_pct">Contact %</SortableTh>
                  <SortableTh {...thProps("call_converted")} data-testid="scorecard-th-call_converted">Call conv</SortableTh>
                  <SortableTh {...thProps("sms_converted")} data-testid="scorecard-th-sms_converted">SMS conv</SortableTh>
                  <SortableTh {...thProps("total_converted")} data-testid="scorecard-th-total_converted">Total conv</SortableTh>
                  <SortableTh {...thProps("conv_of_contactable_pct")} data-testid="scorecard-th-conv_of_contactable_pct">Conv/contact %</SortableTh>
                  <SortableTh {...thProps("voice_ratio_of_conversions")} data-testid="scorecard-th-voice_ratio_of_conversions">Voice %</SortableTh>
                  <SortableTh {...thProps("sms_ratio_of_conversions")} data-testid="scorecard-th-sms_ratio_of_conversions">SMS %</SortableTh>
                  <SortableTh {...thProps("avg_time_to_convert_days")} data-testid="scorecard-th-avg_time_to_convert_days">Avg TTC</SortableTh>
                  <SortableTh {...thProps("scorecard_score")} data-testid="scorecard-th-scorecard_score">Score</SortableTh>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.agent_id} className="border-b border-slate-50/70 hover:bg-slate-50/40"
                      data-testid={`scorecard-row-${r.agent_id}`}>
                    <td className="py-2 px-2">
                      <Link
                        to={`/ops/agents/${encodeURIComponent(r.agent_id || "")}`}
                        className="font-medium text-slate-800 hover:text-[#0A84FF]"
                        data-testid={`scorecard-agent-${r.agent_id}`}
                      >
                        {r.agent_name || "—"}
                      </Link>
                      <div className="text-[10px] text-slate-500">{r.team_name}</div>
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(r.total_calls)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtPct(r.contactable_pct)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(r.call_converted)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(r.sms_converted)}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-semibold text-slate-900">{fmt(r.total_converted)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtPct(r.conv_of_contactable_pct)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtPct(r.voice_ratio_of_conversions)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtPct(r.sms_ratio_of_conversions)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">
                      {fmtDays(r.avg_time_to_convert_days)}
                      {r.conversions_measured_for_ttc ? (
                        <div className="text-[10px] text-slate-400">n={r.conversions_measured_for_ttc}</div>
                      ) : null}
                    </td>
                    <td className="py-2 px-2"><ScoreBar value={r.scorecard_score} /></td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={11} className="py-8 text-center text-slate-500">
                      {loading ? <><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</> : "No agents match the current filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </LoadingOverlay>
    </div>
  );
}
